import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { CentsDataGridServer } from 'components/common/dataGrid/dataGridServer';
import TableSearchBar from 'components/common/search/TableSearchBar';
import { DEFAULT_PAGE_LIMIT } from 'utils/constants';
import { getPayments } from 'api/admin/payments/payments';

const COLUMNS = [
  { headerName: 'Payment ID', field: 'id', flex: 0.75, sortable: false },
  { headerName: 'Store Name', field: 'storeName', flex: 1, sortable: false },
  { headerName: 'Order Code', field: 'orderCode', flex: 1, sortable: false },
  { headerName: 'Creation Date', field: 'createdAt', flex: 1.5, sortable: false },
  { headerName: 'Processor', field: 'paymentProcessor', flex: 1, sortable: false },
  { headerName: 'Status', field: 'status', flex: 1, sortable: false },
  { headerName: 'Timing', field: 'paymentTiming', flex: 1, sortable: false },
  { headerName: 'Total Paid', field: 'totalAmount', flex: 1, sortable: false },
];

const PaymentsTable = () => {
  const history = useHistory();
  const [pageNumber, setPageNumber] = useState(0);
  const [searchString, setSearchString] = useState('');
  const [rowCount, setRowCount] = useState(0);

  const { data, isLoading } = useQuery(
    ['searchPayments', { searchString, pageNumber }],
    async () => {
      const { data } = await getPayments(pageNumber, searchString);

      /**
       * due to performance issues, our API to fetch payments now only returns 20 payments as the total
       * however, we want to make sure we can click to the next page of payments in datagrid
       *
       * so here, we're tricking datagrid into thinking there are more payments to fetch by modifying the rowCount
       *
       * if the returned total is 20, that means there are likely more results to fetch and the API returned the max
       * in that case, we take 20, multiply it by the page number + 1 (since page number starts at 0), and add 1,
       * making the total rowCount of 21.
       *
       * if the total is not 20, then we know we've reached the end of the results, and we set the rowCount to the total
       */
      const adjustedPaginationTotal =
        data.total === DEFAULT_PAGE_LIMIT ? (pageNumber + 1) * DEFAULT_PAGE_LIMIT + 1 : data.total;
      setRowCount(adjustedPaginationTotal);

      return {
        payments: data.payments,
        paymentCount: data.total,
      };
    },
    {
      onError: (error) => {
        // maintaining existing error handling behavior
        console.log(error);
        Sentry.captureException(error);
      },
    },
  );
  const { payments } = data ?? {};

  const handleSearch = (searchInput) => {
    setSearchString(searchInput);
    setPageNumber(0);
  };

  const handleRowClick = (payment) => {
    const paymentData = payment.row;
    const searchParams = new URLSearchParams();

    if (paymentData.orderId) {
      searchParams.set('orderId', paymentData.orderId);
    }
    history.push({
      pathname: `/payments/${paymentData.id}`,
      search: searchParams.toString(),
    });
  };

  return (
    <div className="page-container">
      <h1 className="payments-header">Payments</h1>
      <div className="payments-search-bar">
        <TableSearchBar value={searchString} placeholder="Find a payment" onSearch={handleSearch} />
      </div>
      <CentsDataGridServer
        tableName="PAYMENTS"
        columns={COLUMNS}
        data={payments ?? []}
        rowCount={rowCount}
        pageSize={DEFAULT_PAGE_LIMIT}
        page={pageNumber}
        onPageChange={({ page }) => setPageNumber(page)}
        onRowClick={handleRowClick}
        isLoading={isLoading}
        getRowId={(payment) => payment.orderPaymentId ?? payment.id}
      />
    </div>
  );
};

export default PaymentsTable;
